// Unified analytics helper — Google Analytics 4 (gtag), Microsoft Clarity, and the
// Meta (Facebook) Pixel. Safe no-op when a provider isn't available (ad-blockers, local
// dev, consent off) — analytics must NEVER break the user experience.

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const META_ENABLED = String(process.env.REACT_APP_META_TRACKING_ENABLED || "true").toLowerCase() !== "false";

const CONSENT_KEY = "ns_ad_consent"; // 'granted' | 'denied'

// ---- Advertising consent -------------------------------------------------
export const getAdConsent = () => {
  try { return window.localStorage.getItem(CONSENT_KEY); } catch { return null; }
};
export const hasAdConsent = () => getAdConsent() === "granted";
export const setAdConsent = (granted) => {
  try { window.localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied"); } catch { /* ignore */ }
  if (granted) initConsentedAnalytics(true);
};

// ---- Meta match-key cookies (non-hashed, read client-side) ---------------
const readCookie = (name) => {
  try {
    const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[2]) : undefined;
  } catch { return undefined; }
};
export const getFbpCookie = () => readCookie("_fbp");
export const getFbcCookie = () => readCookie("_fbc");

// ---- Meta Pixel ----------------------------------------------------------
let pixelReady = false;

export const initializeMetaPixel = (fireInitialPageView = true) => {
  try {
    if (!META_ENABLED || !META_PIXEL_ID) return;
    if (!hasAdConsent()) return;            // do not load Pixel before advertising consent
    if (window.fbq && pixelReady) return;   // no duplicate init
    if (!window.fbq) {
      /* eslint-disable */
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n;
        n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
        t = b.createElement(e); t.async = true; t.src = v;
        s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      /* eslint-enable */
      window.fbq("init", META_PIXEL_ID);
    }
    pixelReady = true;
    if (fireInitialPageView) window.fbq("track", "PageView");
  } catch (_) { /* swallow */ }
};

// Legacy alias kept for existing imports.
export const initMetaPixel = () => initializeMetaPixel(true);

// ---- Google Analytics 4 (gtag.js) ---------------------------------------
// Injected dynamically, ONLY after advertising consent is granted. Never loaded
// on "Decline". IDs live here (not hardcoded in index.html) so consent gates them.
const GA4_ID = "G-YNR43MTTFV";
let ga4Ready = false;
export const initializeGA4 = () => {
  try {
    if (!hasAdConsent() || ga4Ready) return;
    ga4Ready = true;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
      window.gtag = function () { window.dataLayer.push(arguments); };
    }
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.gtag("js", new Date());
    window.gtag("config", GA4_ID);
  } catch (_) { /* swallow */ }
};

// ---- Microsoft Clarity ---------------------------------------------------
const CLARITY_ID = "x90k18lemd";
let clarityReady = false;
export const initializeClarity = () => {
  try {
    if (!hasAdConsent() || clarityReady) return;
    clarityReady = true;
    /* eslint-disable */
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_ID);
    /* eslint-enable */
  } catch (_) { /* swallow */ }
};

// Unified consented-analytics bootstrap — GA4, Clarity, and Meta Pixel. Safe no-op
// unless advertising consent === "granted". Call on app load (returning "granted"
// users) and immediately after the user clicks "Accept".
export const initConsentedAnalytics = (fireInitialPageView = true) => {
  if (!hasAdConsent()) return;
  initializeGA4();
  initializeClarity();
  initializeMetaPixel(fireInitialPageView);
};

export const newEventId = () =>
  (typeof window !== "undefined" && window.crypto?.randomUUID?.()) ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const canFireMeta = () => META_ENABLED && !!META_PIXEL_ID && hasAdConsent();

// Fire a standard/custom Meta event. eventId enables Pixel<->CAPI deduplication.
export const trackMetaEvent = (name, params = {}, eventId) => {
  try {
    if (!canFireMeta()) return;
    if (!pixelReady) initializeMetaPixel(false);
    if (typeof window.fbq === "function") {
      window.fbq("track", name, params, eventId ? { eventID: eventId } : undefined);
    }
  } catch (_) { /* swallow */ }
};
// Explicit dedup variant (backend generated the event id).
export const trackMetaEventWithId = (name, eventId, params = {}) => trackMetaEvent(name, params, eventId);

// De-duplicated PageView per route path (guards React Strict Mode double-invokes).
let lastPageViewPath = null;
export const trackMetaPageView = (path) => {
  try {
    if (!canFireMeta()) return;
    if (path && path === lastPageViewPath) return;
    lastPageViewPath = path || null;
    if (!pixelReady) initializeMetaPixel(false);
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  } catch (_) { /* swallow */ }
};

// ViewContent — fire once per key content view (landing / registration).
const viewContentFired = new Set();
export const trackViewContent = (key, eventId, params = {}) => {
  if (viewContentFired.has(key)) return;
  viewContentFired.add(key);
  trackMetaEvent("ViewContent", {
    content_name: "IdeaThon 2026",
    content_category: "Student Innovation Competition",
    ...params,
  }, eventId);
};

// We fire ALL Meta standard events explicitly at their verified trigger points
// (ViewContent, CompleteRegistration, InitiateCheckout, Purchase, Lead). The generic
// track() helper therefore maps nothing to Meta by default.
const META_EVENT_MAP = {};

export const track = (event, params = {}) => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, params);
    }
  } catch (_) { /* swallow */ }

  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function") {
      window.clarity("event", event);
      window.clarity("set", "funnel_stage", event);
    }
  } catch (_) { /* swallow */ }

  const metaEvent = META_EVENT_MAP[event];
  if (metaEvent) trackMetaEvent(metaEvent, {}, params.event_id);
};

// GA4 ecommerce 'purchase' + Meta Pixel 'Purchase' (deduped via event_id = transactionId).
export const trackPurchase = (transactionId, value, currency = "INR") => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "purchase", {
        transaction_id: transactionId,
        value: Number(value) || 0,
        currency,
        items: [{
          item_id: "ideathon_registration",
          item_name: "IdeaThon 2026 Entry Fee",
          price: Number(value) || 0,
          quantity: 1,
        }],
      });
    }
  } catch (_) { /* swallow */ }
  trackMetaEvent("Purchase", {
    content_type: "product",
    content_category: "IdeaThon Registration",
    content_name: "IdeaThon 2026 Entry Fee",
    content_ids: ["ideathon-2026-entry"],
    value: Number(value) || 0,
    currency,
  }, transactionId);
};
